import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchAPI } from "../config/api";
import {
  TeamHeader,
  TeamPercentiles,
  TeamLoading,
  TeamError,
  TabNavigation,
  TeamContent,
  STAT_TYPES,
} from "../components/team";
import { DEFAULT_DIVISION } from "../config/constants";

export const TeamPage = () => {
  const { orgId } = useParams();
  const [teamData, setTeamData] = useState(null);
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(STAT_TYPES.BATTING);
  const [selectedDivision] = useState(DEFAULT_DIVISION);

  useEffect(() => {
    const loadTeamData = async () => {
      setIsLoading(true);
      try {
        const [teamResponse, gamesResponse] = await Promise.all([
          fetchAPI(`/api/team/${orgId}?division=${selectedDivision}`),
          fetchAPI(`/api/team/${orgId}/games?division=${selectedDivision}`).catch(() => []),
        ]);
        setTeamData(teamResponse);
        setGames(gamesResponse || []);
      } catch (err) {
        setError(err.message || "Failed to load team data");
      } finally {
        setIsLoading(false);
      }
    };

    loadTeamData();
  }, [orgId, selectedDivision]);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (teamData?.batting_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.BATTING, label: "Team Batting" });
    }
    if (teamData?.pitching_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.PITCHING, label: "Team Pitching" });
    }
    if (teamData?.batting_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.LINEUP, label: "Lineup" });
    }
    if (teamData?.pitching_stats?.length > 0) {
      tabs.push({ id: STAT_TYPES.BULLPEN, label: "Bullpen" });
    }
    if (teamData?.roster?.length > 0) {
      tabs.push({ id: STAT_TYPES.ROSTER, label: "Roster" });
    }
    if (games?.length > 0) {
      tabs.push({ id: STAT_TYPES.GAMES, label: "Games" });
    }
    return tabs;
  }, [teamData, games]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  if (isLoading) return <TeamLoading />;
  if (error) return <TeamError error={error} />;
  if (!teamData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[6%] right-[8%] w-[380px] h-[380px] bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[12%] left-[6%] w-[520px] h-[520px] bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-[48%] right-[28%] w-[300px] h-[300px] bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse delay-500" />
      </div>

      <div className="relative z-10 container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        {!isHeaderReady && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <TeamHeader teamData={teamData} onReady={() => setIsHeaderReady(true)} />
            </div>
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
              <TeamPercentiles
                orgId={teamData.org_id}
                division={selectedDivision}
                years={teamData.years_played}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
              availableTabs={availableTabs}
            />
            <TeamContent 
              activeTab={activeTab} 
              teamData={teamData} 
              games={games}
              orgId={teamData.org_id}
              division={selectedDivision}
              years={teamData.years_played}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
